import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { environment } from '@environments/environment';
import { Observable, ObservedValueOf, throwError } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import {
  Prize,
  Sponsor,
  Criteria,
  Giveaway,
  Draw,
} from '@app/_models/giveaway';
import { SessionService } from '../session.service';
import { Dictionary } from '@app/_models/shared.model';

@Injectable({
  providedIn: 'root',
})
export class GiveawayService {
  constructor(
    private sessionService: SessionService,
    private http: HttpClient
  ) { }

  getPrizes(): Observable<Prize[]> {
    const url = `${environment.apiUrl}/prizes/`;
    return this.http.get<Prize[]>(url);
  }

  getSponsors(): Observable<Sponsor[]> {
    const url = `${environment.apiUrl}/sponsors/`;
    return this.http.get<Sponsor[]>(url);
  }

  getGiveaway(giveawayId: number): Observable<Giveaway> {
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/`;
    return this.http.get<Giveaway>(url);
  }

  getSponsor(sponsorId: number): Observable<Sponsor> {
    const url = `${environment.apiUrl}/sponsors/${sponsorId}/`;
    return this.http.get<Sponsor>(url);
  }

  getCriterias(): Observable<Criteria[]> {
    const url = `${environment.apiUrl}/criteria/`;
    return this.http.get<Criteria[]>(url);
  }

  getDrawsByGiveaway(giveawayId: number): Observable<Draw[]> {
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/draws/`;
    return this.http.get<Draw[]>(url);
  }
  getPrizeByDraw(prizeId: number): Observable<Prize> {
    const url = `${environment.apiUrl}/prizes/${prizeId}/`;
    return this.http.get<Prize>(url);
  }

  getStates(): Observable<Dictionary[]> {
    const url = `${environment.apiUrl}/lotteries/status/`;
    return this.http.get<Dictionary[]>(url);
  }

  createPrize(newPrize: FormData): Observable<Prize> {
    const url = `${environment.apiUrl}/prizes/`;
    return this.http.post<Prize>(url, newPrize);
  }

  createSponsor(newSponsor: FormData): Observable<Sponsor> {
    const url = `${environment.apiUrl}/sponsors/`;
    return this.http.post<Sponsor>(url, newSponsor);
  }

  createGiveaway(newGiveaway: Giveaway): Observable<Giveaway> {
    const giveaway: Giveaway = {
      ...newGiveaway,
      user: this.sessionService.currentUser.user,
      facility: this.sessionService.currentFacility.id,
    };
    const url = `${environment.apiUrl}/lotteries/`;
    return this.http.post<Giveaway>(url, giveaway);
  }

  executeGiveaway(id: number): Observable<Giveaway> {
    const url = `${environment.apiUrl}/lotteries/${id}/execute`;
    return this.http.get<Giveaway>(url);
  }

  createDraw(newDraw: Draw, giveawayId: number): Observable<Draw> {
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/draws/`;
    return this.http.post<Draw>(url, newDraw);
  }

  updateDraw(draw: Draw, giveawayId: number, drawId: number): Observable<Draw> {
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/draws/${drawId}/`;
    return this.http.put<Draw>(url, draw);
  }

  publishGiveaway(giveawayId: number) {
    const user = this.sessionService.currentUser.user;
    const facility = this.sessionService.currentFacility.id;
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/publish/`;
    return this.http.get(url, {
      params: new HttpParams()
        .set('user', user.toString())
        .set('facility', facility.toString()),
    });
  }

  updateGiveaway(
    giveawayId: number,
    newGiveaway: Giveaway
  ): Observable<Giveaway> {
    const giveaway: Giveaway = {
      ...newGiveaway,
      user: this.sessionService.currentUser.user,
      facility: this.sessionService.currentFacility.id,
    };
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/`;
    return this.http.put<Giveaway>(url, giveaway);
  }

  cancelGiveaway(giveawayId: number): Observable<string> {
    const user = this.sessionService.currentUser.user;
    const facility = this.sessionService.currentFacility.id;
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/cancel/`;
    return this.http.get<string>(url, {
      params: new HttpParams()
        .set('user', user.toString())
        .set('facility', facility.toString()),
    });
  }

  deliverPrize(
    giveawayId: number,
    drawId: number,
    code: string
  ): Observable<string> {
    const user = this.sessionService.currentUser.user;
    const facility = this.sessionService.currentFacility.id;
    const url = `${environment.apiUrl}/lotteries/${giveawayId}/draws/${drawId}/deliver/`;
    return this.http.get<string>(url, {
      params: new HttpParams()
        .set('user', user.toString())
        .set('facility', facility.toString())
        .set('code', code.toString()),
    });
  }

  findGiveaways(
    pageNumber,
    pageSize = 10,
    status = -1
  ): Observable<Giveaway[]> {
    const url = `${environment.apiUrl}/lotteries/`;

    return this.http.get<Giveaway[]>(url, {
      params: new HttpParams()
        .set('pageNumber', pageNumber.toString())
        .set('pageSize', pageSize.toString())
        .set('status', status.toString()),
    });
  }
}
